import angular from "angular";

import {
  ApiListItemValidation, BootstrapStyle, calcReportType, Calculation, CalculationResult, CalculationResultLogic, Calculations,
  CalendarEvents, Dates, Employments, InputEnumOption, MessageFrom, MessageThreadStatus, Objects, ODataResult, PaymentChannel, Payroll03Info, PayrollDetails,
  Payrolls, PayrollStatus, SalaryDateKind, SalaryDateLogic, ThreadedMessageType, Translations, TaxcardUiInfo, TaxCard2019Logic, IApiListItem, PayrollListItem,
  Taxcards,
  EmploymentRelationTaxcard, HolidayCode, Arrays,
  AbsenceCauseCode
} from "@salaxy/core";

import { InvoicesService, ReportsService, SessionService, UiCrudHelpers, UiHelpers } from "../../services";
import { ApiCrudObjectController } from "../bases";
import { PayrollLogic } from "./PayrollLogic";
import { MessageThreadCrudController } from "../communications";

/**
 * Payroll (Palkkalista) is a list of employees who receive salary or wages from a particular organization.
 * Typical usecase is that a a company has e.g. a monthly salary list that is paid
 * at the end of month. For next month, a copy is then made from the latest list and
 * the copy is potentially modified with the changes of that particular month.
 */
export class PayrollCrudController extends ApiCrudObjectController<PayrollDetails, PayrollListItem> {

  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["Payrolls", "UiHelpers", "UiCrudHelpers", "$location", "$routeParams", "Calculations", "ReportsService", "SessionService", "InvoicesService", "CalendarEvents", "Employments", "$timeout", "Taxcards"];

  /** Index of the active tab in the combined edit view. */
  public activeTab = "properties";

  /** Flags to indicate if the request is in progress */
  public loaders: any = {};

  /**
   * Action buttons that are visible in the user interface.
   * Default is "copy", "cancel". If read only is forced neither is included.
   * TODO: Current implementation is for read-only view only.
   */
  public actions: ("none" | "copy" | "pay" | "cancel")[];

  /** All employment list items related to all calcs in payroll. */
  private _employments: { [key: string]: IApiListItem } = {};

  /** TODO: Items to refactor before going to production. */
  /** Cache for changedCalcCount so that it is called only once within on $digest loop. */
  protected changedCalcCountCache;

  /** User interface related properties. */
  private calcUiProperties: {
    /** Calculation ID */
    id: string,
    /** If true, the calc is in edit mode. */
    isInEdit: boolean,
    /** If true, the calculation is in collapsed mode (not showing the details) */
    isCollapsed: boolean,
  }[] = [];

  /**
   * Reference to the latest calculation result
   */
  private _calcsResult: CalculationResult;

  /**
   * Reference to empty calculation array.
   * Prevents infinite $digest iterations, when the reference to empty array does not change.
   */
  private emptyCalcList: Calculation[] = [];

  /** Available channels */
  private _availableChannels: Array<{ value: string, text: string }> = [];

  constructor(
    private payrollsApi: Payrolls,
    uiHelpers: UiHelpers,
    private uiCrudHelpers: UiCrudHelpers,
    $location: angular.ILocationService,
    $routeParams: any,
    private calcsApi: Calculations,
    private reportsService: ReportsService,
    private sessionService: SessionService,
    private invoicesService: InvoicesService,
    private calendarEvents: CalendarEvents,
    private employmentsApi: Employments,
    private $timeout: angular.ITimeoutService,
    private taxcardsApi: Taxcards,
  ) {
    // Dependency injection
    super(payrollsApi, uiHelpers, $location, $routeParams);
  }

  /**
   * Initialization code.
   */
  public $onInit() {
    super.$onInit();
    if (this.isReadOnlyForced) {
      this.activeTab = "reports";
    }
  }

  /** Catched the return of reload to update values. */
  public reload(): Promise<PayrollDetails> {
    return super.reload().then((payroll) => {
      if (super.isNew()) {
        this.setPaymentChannel(payroll);
      }
      return payroll;
    });
  }

  /** Implements the getDefaults of ApiCrudObjectController */
  public getDefaults() {
    return {
      listUrl: this.listUrl || "/payroll",
      detailsUrl: this.detailsUrl || "/payroll/details/",
      oDataTemplateUrl: "salaxy-components/odata/lists/Payrolls.html",
      oDataOptions: {},
    };
  }


  /**
   * Returns true, if the given action button should be shown
   * @param action One of the action values: See actions field type for supported values.
   */
  public showAction(action: string): boolean {
    const actions = this.actions?.length ? this.actions :
      this.isReadOnlyForced ? [] : ["copy", "cancel"];

    if (action === "pay") {
      // Check action and status
      return !!actions.find(x => x == action) &&
      ( !!this.current &&
        ( this.current.info.status === PayrollStatus.Draft ||
          this.current.info.status === PayrollStatus.WaitingApproval
        )
      );
    }

    return !!actions.find(x => x == action);
  }

  /** Returns true if any calculations have been added to the Payroll */
  public get hasCalculations() {
    return this.current && this.current.calcs.length > 0;
  }

  /** Save changes to the payroll as well as to changed calculations */
  public save(): Promise<PayrollDetails> {
    if (this.changedCalcCount > 0) {
      // Just to not show "Saving calculations..."
      return this.commitAllCalcs().then(() => {
        return super.save();
      });
    }
    return super.save();
  }

  /** Recalculate and save worktimes of all calculations. */
  public recalculateWorktimes(): Promise<Calculation[]> {
    const loader = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
    const recalcs: Promise<Calculation>[] = this.calcs.map((apiCalc) => {
    return this.calcsApi.getWorktimeData(apiCalc).then((worktime) => {
      apiCalc.worktime = worktime;
      return this.calcsApi.recalculateWorktime(apiCalc).then((resultCalc) => {
          return angular.copy(resultCalc, apiCalc);
        });
      });
    });
    return Promise.all(recalcs).then((result) => {
      loader.dismiss();
      return result;
    });
  }

  /**
   * Calculates the total amount of days for the given period type.
   * @param calc Calculation to calculate days for.
   * @param periodType Period type to calculate days for.
   * @returns Sum of days for the given period type.
   */
  public calculatePeriodDays(calc: Calculation, periodType: "absencesDays"  | "leavesDays" | "paidAbsencesDays" | "accruedDays") {
    switch (periodType) {
      case "leavesDays":
        return Arrays.sum(calc.worktime?.absencesPeriods?.filter(x => x.causeCode == AbsenceCauseCode.AnnualLeave) ?? [], (x) => x.period.daysCount);
      case "absencesDays":
        return Arrays.sum(calc.worktime?.absencesPeriods?.filter(x => x.causeCode != AbsenceCauseCode.AnnualLeave && !x.isPaid) ?? [], (x) => x.period.daysCount);
      case "paidAbsencesDays":
        return Arrays.sum(calc.worktime?.absencesPeriods?.filter(x => x.causeCode != AbsenceCauseCode.AnnualLeave && x.isPaid) ?? [], (x) => x.period.daysCount);
      case "accruedDays":
        if (this.showAccruals(calc)) {
          return Arrays.sum(calc.worktime?.accruals, (x) => x.daysAccrued);
        }
        return 0;
    }
  }

  /**
   * Returns true if the accruals are accumulated.
   * @param calc Calculation.
   * @returns Returns true if the accruals are accumulated.
   */
  public showAccruals(calc: Calculation) {
    return calc.worktime?.accrualSpec?.code ==  HolidayCode.Permanent14Days || calc.worktime?.accrualSpec?.code ==  HolidayCode.Permanent35Hours || calc.worktime?.accrualSpec?.code ==  HolidayCode.TemporaryTimeOff || calc.worktime?.accrualSpec?.code == HolidayCode.Other || calc.worktime?.accrualSpec?.code == HolidayCode.Script;
  }

  /**
   * Creates a copy of a payroll
   * This is a synchronous method that should basically convert a saved item to a new item.
   * @param copySource Item (container item) to copy as new.
   */
  public copyItem(copySource: PayrollDetails): PayrollDetails {
    const oldId = copySource.id;
    const copy = super.copyItem(copySource);
    copy.info.messageThreadId = null;
    if (copy.info.status === PayrollStatus.Template) {
      copy.input.template = oldId;
      copy.info.status = PayrollStatus.Draft;
    }
    // HACK: remove payroll-service flag. This will be replaced with invoicing flags eventually.
    if (copy.usecase &&
      (copy.usecase.uri === "palkkaus.fi/usecases/payroll-service/pro-calc" ||
        copy.usecase.uri === "palkkaus.fi/usecases/payroll-service/calc")) {
      copy.usecase = {};
    }
    return copy;
  }

  /**
   * Reloads the validations and info for the current item.
   * These are constructed based on calculations data and if changed, will update the Payroll in storage.
   */
  public reloadValidation() {
    // TODO: Add indication that validation is being updated.
    // TODO: This would become obsolete when we separate calculations modification to a separate controller.
    if (this.currentId) {
      this.api.getSingle(this.currentId).then((payroll) => {
        this.current.validations = payroll.validations;
        this.current.info = payroll.info;
        this.original.validations = payroll.validations;
        this.original.info = payroll.info;
      });
    }
  }

  /** Gets a validation object for a calculation */
  public getValidation(calc: Calculation): ApiListItemValidation {
    return this.current.validations.find((x) => x.id === calc.id) || {
      id: calc.id, // Paid Payrolls will not have validations.
      isValid: true,
    };
  }

  /** UI related data and texts about the taxcard. */
  public getTaxUi(calc: Calculation): TaxcardUiInfo {
    return TaxCard2019Logic.getCalcTaxcardInfo(calc);
  }

  /** Show add new taxcard dialog */
  public async showAddNewTaxcard(calc: Calculation) {
    const empl = await this.employmentsApi.getSingle(calc.worker.employmentId);
    if (!empl) {
      return;
    }

    // try to set to auto as first
    const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
    if(empl.info.taxcard==EmploymentRelationTaxcard.Undefined || empl.info.taxcard==EmploymentRelationTaxcard.Auto){
      const taxcard = await this.taxcardsApi.setAuto(calc.worker.paymentData.socialSecurityNumberValid);
      if (taxcard) {
        await this.commitCalc(calc, false, true);
        loading.dismiss();
        return;
      }
    }

    const result = await this.uiCrudHelpers.createNewTaxcard(calc.worker.paymentData.socialSecurityNumberValid);
    if (result.action === "ok") {
      await this.commitCalc(calc, false, true);
      loading.dismiss();
      return;
    }

    loading.dismiss();
  }

  /**
   * Returns true if the date is overdue (smaller than today).
   * @param date Date to compare to today.
   */
  public isOverdue(date: string): boolean {
    date = Dates.asDate(date);
    return date < Dates.getToday();
  }

  /** Gets the list of calculations in this payroll object */
  public get calcs(): Calculation[] {
    return this.current ? this.current.calcs : this.emptyCalcList;
  }

  /** Returns the constantly updating info component from the open calculations (without round-trip to server) */
  public get info(): Payroll03Info {
    return PayrollLogic.getInfo(this.current, this.calcs);
  }

  /** Summarized CalculationResult for all calculations in the payroll. */
  public get calcsResult(): CalculationResult {
    const result = CalculationResultLogic.add(this.calcs.map((x) => x.result));
    if (!angular.equals(result, this._calcsResult)) {
      this._calcsResult = result;
    }
    return this._calcsResult;
  }

  /** Returns true if there is no net salary payment to worker at all. */
  public get isCurrentZeroPayment(): boolean {
    return PayrollLogic.isZeroPayment(this.calcs ?? []);
  }

  /** Disable function for the salary date picker. */
  public isDisabledSalaryDate = (date): boolean => {
    const role = this.sessionService.isInRole("pro") ? "pro" :
      this.sessionService.isInRole("admin") ? "admin" : null;
    switch (this.current.input.salaryDateKind) {
      case SalaryDateKind.InPast:
        return false;
      case SalaryDateKind.Default:
      default:
        return !SalaryDateLogic.isValidSalaryDate(date, null, null, true, this.current.input.paymentChannel, PayrollLogic.isZeroPayment(this.calcs ?? []), role);
    }
  };

  /**
   * Checks if user is in one of the given roles.
   * @param roles A comma separated list of roles, potentially with exlamation mark
   */
  public isInSomeRole(roles: string): boolean {
    return this.sessionService.isInSomeRole(roles);
  }

  /** Gets the UI properties for a calculation */
  public getUi(calc: Calculation): {
    /** Calculation ID */
    id: string,
    /** If true, the calc is in edit mode. */
    isInEdit: boolean,
    /** If true, the calculation is in collabsed mode (not showing the details) */
    isCollapsed: boolean,
  } {
    if (!calc || !calc.id) {
      return {} as any;
    }
    let ui = this.calcUiProperties.find((x) => x.id === calc.id);
    if (ui == null) {
      ui = {
        id: calc.id,
        isCollapsed: true,
        isInEdit: false,
      };
      this.calcUiProperties.push(ui);
    }
    return ui;
  }

  /**
   * Set calculation rows editable
   */
  public setRowsEditable(calc: Calculation | "all") {
    if (calc === "all") {
      this.setUiForAll("isInEdit", true);
      this.setUiForAll("isCollapsed", false);
    } else {
      this.getUi(calc).isCollapsed = false;
      this.getUi(calc).isInEdit = false; // First to false to assure firing of the setter.
      this.getUi(calc).isInEdit = true;
    }
  }

  /**
   * Sets a UI property for all calculations.
   * @param prop Name of the property to set.
   * @param value New value for the property.
   */
  public setUiForAll(prop: "isCollapsed" | "isInEdit", value: boolean) {
    this.calcs.forEach((calc) => {
      this.getUi(calc)[prop] = value;
    });
  }

  /**
   * Recalculates and saves calculation to the server.
   * @param calc Calculation to save.
   * @param noReloadValidation If true, the parent should call reloadValidation().
   * @param forceSave If true, changes are saved even when there are no changes.
   */
  public commitCalc(calc: Calculation, noReloadValidation = false, forceSave = false): Promise<{
    /** Calculation (after save, if saved) */
    calc: Calculation,
    /** True, if the calculation was saved. Unchanged calculations are not saved. */
    isSaved: boolean,
  }> {
    this.getUi(calc).isInEdit = false;
    if (forceSave || this.hasCalcChanges(calc)) {
      return this.payrollsApi.calculationSave(calc, this.current.id).then((result) => {
        if (!noReloadValidation) {
          this.reloadValidation();
        }
        return this.setCalc(result);
      });
    }
    return Promise.resolve({ calc, isSaved: false });
  }

  /** Sets the calculation after round-trip from the server. */
  public setCalc = (newCalc: Calculation) => {
    const oldCalc = this.current.calcs.find((x) => x.id === newCalc.id);
    const originalCalc = this.original.calcs.find((x) => x.id === newCalc.id);
    angular.copy(newCalc, oldCalc);
    angular.copy(newCalc, originalCalc);
    return { calc: newCalc, isSaved: true };
  };

  /**
   * Reset any modifications to a calculation.
   * @param calc Calculation that was edited.
   */
  public resetCalc(calc: Calculation) {
    this.getUi(calc).isInEdit = false;
    if (this.hasCalcChanges(calc)) {
      const originalCalc = this.original.calcs.find((x) => x.id === calc.id);
      angular.copy(originalCalc, calc);
    }
  }

  /** Gets the count of changed calculations. */
  public get changedCalcCount() {
    // Cache within one $digest loop.
    this.changedCalcCountCache = this.calcs.filter((x) => this.hasCalcChanges(x)).length;
    return this.changedCalcCountCache;
  }

  /** Returns true if a calculation has changes to its original. */
  public hasCalcChanges(calc: Calculation): boolean {
    // TODO: Move this check to be run in every $digest loop
    const originalCalc = this.original.calcs.find((x) => x.id === calc.id);
    if (!originalCalc) {
      throw new Error("Unexpected: No original calc in hasCalcChanges.");
    }
    return !angular.equals(originalCalc, calc);
  }

  /**
   * Commits changes to all calculations that are in edit mode.
   * @returns A promise with the number of saved (changed) calculations.
   */
  public commitAllCalcs(): Promise<number> {
    const loader = this.uiHelpers.showLoading("SALAXY.NG1.PayrollComponent.commitAllCalcs.heading");
    this.setUiForAll("isCollapsed", true);
    this.setUiForAll("isInEdit", false);
    const promises = this.calcs.map((x) => this.commitCalc(x, true));
    return Promise.all(promises).then((result) => {
      loader.dismiss();
      this.reloadValidation();
      return result.filter((x) => x.isSaved).length;
    });
  }

  /** Resets all calculations to their unsaved state. */
  public resetAllCalcs() {
    this.calcs.forEach((x) => this.resetCalc(x));
  }

  /**
   * Opens DateRange component in modal dialog.
   * If daterange is changed asks whether all calculations should be changed to a new period.
   * Saves changes to server.
   */
  public showDateRange() {
    this.uiHelpers.showDateRange(this.current.input.period, "SALAXY.NG1.PayrollComponent.showDateRangeTitle", "SALAXY.UI_Terms.save").then((result) => {
      if (result.result === "ok" && result.hasChanges && this.calcs.length > 0) {
        this.uiHelpers.showConfirm("SALAXY.NG1.PayrollComponent.showDateRangeDialogTitle",
          "SALAXY.NG1.PayrollComponent.showDateRangeDialogDescr",
          "SALAXY.UI_Terms.yes", "SALAXY.UI_Terms.no").then((value) => {
            if (value) {
              this.calcs.forEach((calc) => {
                calc.info.workStartDate = this.current.input.period.start;
                calc.info.workEndDate = this.current.input.period.end;
                calc.framework.numberOfDays = this.current.input.period.daysCount;
              });
              this.save();
            } else {
              super.save();
            }
          });
      }
    });
  }

  /**
   * Shows a DateRange edit component for a single calculation.
   * @param calc Calculation to modify.
   */
  public showDateRangeForCalculation(calc: Calculation) {
    this.uiHelpers.showDateRange(calc, "SALAXY.NG1.PayrollComponent.showDateRangeTitle", "SALAXY.UI_Terms.ok");
  }

  /**
   * Shows the calculation in a new dialog.
   * @param calc Calculation to modify / view in dialog.
   */
  public showEditCalcDialog(calc: Calculation, dialog: "default" | "ir" = "default"): Promise<Calculation> {
    const template = dialog === "ir" ? "salaxy-components/modals/calc/CalcIr.html" : "salaxy-components/modals/calc/Calc.html";
    const title = dialog === "ir" ? Translations.get("SALAXY.NG1.CalcReportsComponent.irRows") : Translations.get("SALAXY.NG1.PayrollComponent.showEditCalcDialog");
    return this.uiHelpers.openEditDialog(
      template,
      calc,
      {
        title,
      },
      "xl",
    ).then((result) => {
      if (result.action === "ok") {
        if (result.hasChanges) {
          return this.commitCalc(result.item).then((result) => {
            return result.calc;
          });
        } else {
          return calc;
        }
      } else if (result.action === "delete") {
        this.deleteCalc(calc);
        return null;
      } else {
        this.resetCalc(calc);
        return calc;
      }
    });
  }

  /**
   * Shows a dialog for editing an existing worker.
   * @param calc - Calculation that should be edited
   */
  public showEditWorkerDialog(calc: Calculation): void {
    if (!calc.worker?.accountId) {
      this.showAddWorkerDialog(calc);
    }
    else {
      this.uiCrudHelpers.openEditEmploymentDialog(calc.worker.employmentId, "updateCalc").then((result) => {
        if (result.action === "ok" || result.action === "ok-no-rows") {
          const loader = this.uiHelpers.showLoading("SALAXY.NG1.PayrollComponent.showEditWorkerDialogUpdating");
          this.calcsApi.updateFromEmployment(calc, true, result.action === "ok").then((result2) => {
            this.reloadValidation();
            this.setCalc(result2);
            loader.dismiss();
          });
        }
      });
    }
  }

  /**
   * Opens a list of workers in to a dialog window.
   * Edits the  calculation for the selected worker and current payroll.
   */
  public showAddWorkerDialog(calc: Calculation): void {
    const workerList = [];
    this.uiHelpers.openEditDialog(
      "salaxy-components/modals/calc/WorkerSelect.html",
      workerList,
      {
        title: "SALAXY.NG1.PayrollComponent.validate.addWorker",
      }).then((result) => {
        if (result.action === "ok" && result.item.length == 1) {
          const loader = this.uiHelpers.showLoading("SALAXY.NG1.PayrollComponent.showEditWorkerDialogUpdating");
          calc.worker.employmentId = result.item[0].id;
          this.calcsApi.updateFromEmployment(calc, true, false).then((result2) => {
            this.reloadValidation();
            this.setCalc(result2);
            loader.dismiss();
          });
        }
      });
  }

  /**
   * Shows a dialog during the check of auto taxcard.
   */
  public showRefreshAutoTaxcardDialog(calc: Calculation) {
    this.loaders["showRefreshAutoTaxcardDialog"] = true;
    this.uiCrudHelpers.refreshAutoTaxcard(calc.worker.tax?.taxcardId, calc.workflow?.salaryDate).then(() => {
      this.commitCalc(calc, false, true);
      this.loaders["showRefreshAutoTaxcardDialog"] = false;
    });
  }

  /**
   * Shows a confirm dialog if payroll has any calculations.
   * Returns true if a user clicks OK -button. Otherwise (cancel or close) returns false.
   * @returns True if the user confirmed the operation.
   */
  public importEmploymentDataAll(): Promise<boolean> {
    const payroll = this.current as any;
    if (!payroll.info.calcCount || payroll.info.calcCount === 0) {
      return this.uiHelpers.showAlert("SALAXY.NG1.PayrollComponent.importEmploymentDataAllAlertTitle", "SALAXY.NG1.PayrollComponent.importEmploymentDataAllAlertDescr").then(() => false);
    } else {
      return this.uiHelpers.showConfirm(
        "SALAXY.NG1.PayrollComponent.importEmploymentDataAllConfirmTitle",
        "SALAXY.NG1.PayrollComponent.importEmploymentDataAllConfirmDescr",
        "SALAXY.UI_Terms.continue",
        "SALAXY.UI_Terms.cancel",
      ).then((result: boolean) => {
        if (result) {
          const loader = this.uiHelpers.showLoading("SALAXY.NG1.PayrollComponent.importEmploymentDataAllLoading");
          const promises = this.calcs.map((x) => this.calcsApi.updateFromEmployment(x, true, true).then((result) => this.setCalc(result)));
          return Promise.all(promises).then(() => {
            loader.dismiss();
            this.reloadValidation();
            return true;
          });
        } else {
          return false;
        }
      });
    }
  }

  /**
   * Adds default rows (SalaryDefault) from employment information to calculation.
   * Also saves the calculation
   * @param calc The calculation to update.
   */
  public importEmploymentData(calc: Calculation): Promise<{
    /** Calculation (after save, if saved) */
    calc: Calculation,
    /** True, if the calculation was saved. Unchanged calculations are not saved. */
    isSaved: boolean,
  }> {
    return this.uiHelpers.showConfirm(
      "SALAXY.NG1.PayrollComponent.importEmploymentDataTitle",
      "SALAXY.NG1.PayrollComponent.importEmploymentDataDescr",
      "SALAXY.UI_Terms.continue",
      "SALAXY.UI_Terms.cancel",
    ).then((result: boolean) => {
      if (result) {
        const loader = this.uiHelpers.showLoading("SALAXY.NG1.PayrollComponent.importEmploymentDataLoading");
        return this.calcsApi.updateFromEmployment(calc, true, true).then((result) => {
          loader.dismiss();
          this.reloadValidation();
          return this.setCalc(result);
        });
      } else {
        return Promise.resolve({ calc, isSaved: false });
      }
    });
  }

  /**
   * Show confirm dialog and export employment data from calculation to Employment relation.
   * If the calculation has unmodified changes, the calculation is saved.
   * @param calc Calculation that should be exported to Employment.
   * @returns True if the user confirmed the operation.
   */
  public exportEmploymentData(calc: Calculation): Promise<boolean> {
    return this.uiHelpers.showConfirm(
      "SALAXY.NG1.PayrollComponent.exportEmploymentDataTitle",
      "SALAXY.NG1.PayrollComponent.exportEmploymentDataDescr",
      "SALAXY.UI_Terms.continue",
      "SALAXY.UI_Terms.cancel",
    ).then((result: boolean) => {
      if (result) {
        const loader = this.uiHelpers.showLoading("SALAXY.NG1.PayrollComponent.exportEmploymentDataLoading");
        this.exportEmploymentDataNoConfirm(calc).then((resultInner) => {
          if (resultInner.isSaved) {
            this.reloadValidation();
          }
          loader.dismiss();
          return true;
        });
      } else {
        return false;
      }
    });
  }

  /**
   * Deletes the calculation
   * @param calc Calculation to delete
   * @param unlink If true, will not delete the actual calculation, but instead unlinks it as a separate calculation outside this payroll.
   */
  public deleteCalc(calc: Calculation, unlink = false) {

    if (unlink) {
      this.uiHelpers.showConfirm("SALAXY.NG1.PayrollComponent.payrollCalcs.unlinkCalcDialog.heading",
        "SALAXY.NG1.PayrollComponent.payrollCalcs.unlinkCalcDialog.intro",
        "SALAXY.NG1.PayrollComponent.payrollCalcs.unlinkCalcDialog.confirmButton",
        "SALAXY.NG1.PayrollComponent.payrollCalcs.unlinkCalcDialog.cancelButton")
        .then((result: boolean) => {
          if (result) {
            const loader = this.uiHelpers.showLoading("SALAXY.NG1.PayrollComponent.payrollCalcs.unlinkCalcDialog.loading");
            this.payrollsApi.unlinkCalculations(this.current, [calc.id])
              .then((savedPayroll) => {
                this.setCurrentValue(savedPayroll, true);
                loader.dismiss();
              });
          }
        });
    } else {
      this.uiHelpers.showConfirm(
        "SALAXY.NG1.PayrollComponent.payrollCalcs.deleteCalcDialog.heading",
        this.changedCalcCount > 0 ? "SALAXY.NG1.PayrollComponent.payrollCalcs.deleteCalcDialog.introChanges" : "SALAXY.NG1.PayrollComponent.payrollCalcs.deleteCalcDialog.introNoChanges",
        "SALAXY.NG1.PayrollComponent.payrollCalcs.deleteCalcDialog.confirmButton",
        "SALAXY.NG1.PayrollComponent.payrollCalcs.deleteCalcDialog.cancelButton")
        .then((result: boolean) => {
          if (result) {
            const loader = this.uiHelpers.showLoading("SALAXY.NG1.PayrollComponent.payrollCalcs.deleteCalcDialog.loading");
            this.payrollsApi.deleteCalculations(this.current, [calc.id])
              .then((savedPayroll) => {
                this.setCurrentValue(savedPayroll, true);
                loader.dismiss();
              });
          }
        });
    }
  }

  /**
   * Gets the URL for a payroll pdf.
   * If report has not been saved (ID is null), returns null.
   * @returns Url for specified report
   */
  public getPdfUrl() {
    if (!this.current) {
      return null;
    }
    return this.reportsService.getPdfUrlForCalcs("salarySlip", this.current.calcs.filter((x) => x.id).map((x) => x.id), true);
  }

  /**
   * Shows a report for the payroll calculation using a modal dialog.
   * @param reportType - Type of report to show
   * @param calculation - Calculation for the report
   */
  public showReport(reportType: calcReportType, calculation: Calculation) {
    this.reportsService.showReportModalForCalc(reportType, calculation);
  }

  /**
   * Opens a list of workers in to a dialog window.
   * Creates a new calculation for the selected worker and current payroll.
   */
  public createNewCalc() {
    const workerList = [];
    this.uiHelpers.openEditDialog(
      "salaxy-components/modals/calc/WorkerList.html",
      workerList,
      {
        title: "SALAXY.NG1.PayrollComponent.createNewCalcDialog",
      }).then((result) => {
        if (result.action === "ok" && workerList.length > 0) {
          const loader = this.uiHelpers.showLoading("SALAXY.NG1.PayrollComponent.createNewCalcLoading");
          this.payrollsApi.addEmployments(this.current, workerList.map((x) => x.id))
            .then((savedPayroll) => {
              this.setCurrentValue(savedPayroll, true);
              loader.dismiss();
            });
        }
      });
  }

  /**
   * Opens the list of calculations into a dialog window for selection and then adds them to payroll
   * @param category Either "paid": Read-only, a copy is added ... or "draft": Editable, moved with status changed.
   */
  public addExistingCalc(category: "paid" | "draft" = "draft") {
    const title = (category === "paid" ? "SALAXY.NG1.PayrollComponent.addExistingCalcCopy" : "SALAXY.NG1.PayrollComponent.addExistingCalcMove");
    this.uiHelpers.openSelectCalcs(category, title).then((result) => {
      if (result.action === "ok" && result.item.length > 0) {
        const loader = this.uiHelpers.showLoading("SALAXY.NG1.PayrollComponent.addExistingCalcLoading");
        this.payrollsApi.addCalculations(this.current, result.item.map((x) => x.id))
          .then((savedPayroll) => {
            this.setCurrentValue(savedPayroll, true);
            loader.dismiss();
          });
      }
    });
  }

  /**
   * Opens a list of payrolls for merging the payroll calculations to the current payroll.
   * @param removeImportedRows If true, the operation removes all the imported rows,
   * meaning all the rows in the target Payroll calcs that have a sourceId.
   */
  public async mergePayroll(removeImportedRows = false): Promise<void> {
    const payrolls = [];
    const result = await this.uiHelpers.openEditDialog("salaxy-components/modals/payroll/PayrollList.html", payrolls, {
      title: "Valitse tuotava palkkalista",
      category: "draft",
    }, "lg");
    if (result.action != "ok" || payrolls.length < 1) {
      return;
    }
    const loader = this.uiHelpers.showLoading("Yhdistetään palkkalistoja");
    const source = await this.payrollsApi.getSingle(payrolls[0].id);
    let target = this.current;
    const targetEmployments = target.calcs.map(x => x.worker.employmentId);
    const newEmployments = new Set(source.calcs.map(x => x.worker.employmentId).filter(x => targetEmployments.indexOf(x) < 0));
    if (newEmployments.size) {
      loader.setText(`Lisätään ${newEmployments.size} työntekijää.`);
      target = await this.payrollsApi.addEmployments(target, [...newEmployments]);
      this.setCurrentValue(target, true);
    }

    const calcsToSave: Calculation[] = [];
    if (removeImportedRows) {
      target.calcs.forEach((calc) => {
        if (calc.rows.find(x => !!x.sourceId)) {
          calc.rows = calc.rows.filter(x => !x.sourceId);
          calcsToSave.push(calc);
        }
      });
    }

    source.calcs.forEach((srcCalc) => {
      const targetCalc = target.calcs.find((x) => x.worker.employmentId == srcCalc.worker.employmentId);
      if (!targetCalc) {
        throw new Error("No calculation: This is unexpected.");
      }
      targetCalc.rows = targetCalc.rows.filter((x) => x.sourceId != srcCalc.id);
      const newRows = Objects.copy(srcCalc.rows);
      newRows.forEach(row => {
        row.sourceId = srcCalc.id;
      });
      targetCalc.rows.push(...newRows);
      if (calcsToSave.indexOf(targetCalc) < 0) {
        calcsToSave.push(targetCalc);
      }
    });
    const promises = calcsToSave.map((calc) => this.payrollsApi.calculationSave(calc, target.id));
    loader.setText(`Tallennetaan ${promises.length} muokattua laskelmaa.`);
    const calcs = await Promise.all(promises);
    calcs.forEach((calc) => {
      this.setCalc(calc);
    });
    this.reloadValidation();
    loader.dismiss();
  }

  /** Returns true if the period of a calculation is different than the period of the Payroll. */
  public isDateDifferent(calc: Calculation) {
    return calc.info.workStartDate !== this.current.input.period.start
      || calc.info.workEndDate !== this.current.input.period.end
      || calc.framework.numberOfDays !== this.current.input.period.daysCount;
  }

  /**
   * If true, the form controls should be read-only (no control at all).
   */
  public get isReadOnly() {
    return super.isReadOnly
      || (
        this.current.info.status === PayrollStatus.PaymentStarted &&
        this.current.input.paymentChannel !== PaymentChannel.Undefined
      )
      || (!this.sessionService.isInRole("pro") && this.current.info.status === PayrollStatus.PaymentStarted);
  }

  /**
   * Exports employment data from calculation to Employment relation.
   * If the calculation has unmodified changes, the calculation is saved.
   * @param calc Calculation that should be exported to Employment.
   */
  private exportEmploymentDataNoConfirm(calc: Calculation): Promise<{
    /** Calculation (after save, if saved) */
    calc: Calculation,
    /** True, if the calculation was saved. Unchanged calculations are not saved. */
    isSaved: boolean,
  }> {
    if (this.hasCalcChanges(calc)) {
      return this.calcsApi.updateToEmployment(calc).then(() => this.commitCalc(calc, true));
    } else {
      return this.calcsApi.updateToEmployment(calc).then(() => {
        return Promise.resolve({ calc, isSaved: false });
      });
    }
  }

  /**
   * Sends a payroll with messaging system and sets
   * @param $msg The MessageThreadCrudController that is used for creating the message thread.
   */
  public sendPayroll($msg: MessageThreadCrudController): Promise<void> {
    if (!$msg.current) {
      $msg.model = "new";
    }
    const model = $msg.current;
    model.otherParty = this.sessionService.session.settings.partner.accountId;
    model.status = MessageThreadStatus.Active;
    model.title = model.title || "Palkkalista " + this.current.input.title;
    model.ownerWorkflow = {
      salaryDate: this.current.info.salaryDate,
    };
    model.messages.push({
      contentId: this.currentId,
      fromParty: this.sessionService.isInRole("pro") ? MessageFrom.OtherParty : MessageFrom.Owner,
      type: ThreadedMessageType.Payroll
    });
    return $msg.save().then((msg) => {
      if (this.current.info.status != PayrollStatus.WaitingApproval) {
        this.current.info.messageThreadId = msg.id;
        this.current.info.status = PayrollStatus.WaitingApproval;
        return this.save().then(() => {
          return;
        });
      } else {
        this.current.info.messageThreadId = msg.id; // This is done by messageThread logic on server => ne need to save.
      }
    });
  }

  /**
   * Gets the message title for the action.
   * TODO: This could be replaced by enums.
   */
  public getTitle(action: string) {
    return this.messageActions.find((x) => x.value == action).title + ": " + this.current.input.title;
  }

  /**
   * Available actions for the message.
   * TODO: This could be replaced by enums or it may come from Service model.
   */
  public messageActions: InputEnumOption[] = [
    {
      text: "Maksa",
      value: "pay",
      title: "Maksa palkkalista",
    },/* {
      text: "Tarkista (hyväksy/hylkää)",
      value: "approve",
      title: "Tarkista palkkalista",
    },*/
    {
      text: "Tiedoksi",
      value: "info",
      title: "Tiedoksi palkkalista",
    }, {
      text: "Vastaa kysymykseen",
      value: "question",
      title: "Kysymyksiä palkkalistasta",
    }
  ];

  /**
   * Deletes a linked message: Both the message thread and msg id in this payroll.
   * @param $msg The MessageThreadCrudController that contains the current message.
   */
  public deleteMessage($msg: MessageThreadCrudController) {
    this.uiHelpers.showConfirm("Haluatko poistaa viestiketjun?", "Haluatko varmasti poistaa viestiketjun?")
      .then((result) => {
        if (result) {
          if ($msg.current) {
            $msg.deleteNoConfirm($msg.current).then(() => {
              this.current.info.messageThreadId = null;
              this.save();
            });
          } else {
            this.current.info.messageThreadId = null;
            this.save();
          }
        }
      });
  }

  /** Shows the paymentd dialog / the invoices UI. */
  public showPaymentDialog() {
    this.invoicesService.showPaymentDialog(this.current);
  }

  private setPaymentChannel(payroll: PayrollDetails) {
    if (payroll && payroll.input) {
      payroll.input.paymentChannel = this.invoicesService.defaultChannel;
    }
  }

  /**
   * Sends the payroll from company to PRO.
   * @param message Message to the accountant
   */
  public sendToPro(message: string): void {
    const loader = this.uiHelpers.showLoading("SALAXY.NG1.PayrollComponent.sendToProSending", "SALAXY.NG1.PayrollComponent.sendToProSendingDescr");
    this.current.info.status = PayrollStatus.Draft;
    this.save().then(() => {

      this.api.saveWorkflowEvent(this.current, {
        type: "PartnerMessageClosed",
        ui: BootstrapStyle.Success,
        message: message ? Translations.get("SALAXY.NG1.PayrollComponent.sendToProMessage") + message : Translations.get("SALAXY.NG1.PayrollComponent.sendToProMessageChecked"),
      }).then(() => {
        loader.dismiss();
        this.$location.path("/");
      });
    });
  }

  /**
   * Cancels the payment or invoice processing for the current payroll.
   */
  public cancel(): Promise<PayrollDetails> {
    const eprWarningMessage = this.invoicesService.getEprWarningForCancellation(this.current.info.salaryDate, this.current.info.status == PayrollStatus.PaymentSucceeded);
    const eprText = eprWarningMessage ? "\n" + eprWarningMessage : "";
    return this.uiHelpers.showConfirm(Translations.get("SALAXY.NG1.CalcResultsComponent.cancelDialogTitle") + "?", Translations.get("SALAXY.NG1.CalcResultsComponent.cancelDialogDescr") + `${eprText}`, "SALAXY.NG1.CalcResultsComponent.cancelDialogYes", "SALAXY.NG1.CalcResultsComponent.cancelDialogNo").then((result) => {
      if (result) {
        const loading = this.uiHelpers.showLoading("SALAXY.UI_Terms.wait");
        return this.payrollsApi.cancel(this.currentId).then((item: PayrollDetails) => {
          this.setCurrentValue(item);
          loading.dismiss();
          return this.current;
        });
      } else {
        return this.current;
      }
    });
  }

  /** Fetches and returns all employment list items related to all calcs in payroll. */
  public get employments(): { [key: string]: IApiListItem } {
    if (this.calcs) {
      const calcsEmploymentIds = this.calcs.map((x) => x.worker.employmentId).filter((x) => !!x);
      if (calcsEmploymentIds.length > 0) {
        const fetchedEmploymenIds = Object.keys(this._employments);
        const missingIds = calcsEmploymentIds.filter((x) => fetchedEmploymenIds.indexOf(x) < 0);
        if (missingIds.length > 0) {
          Object.assign(this._employments, Object.fromEntries(missingIds.map((id) => [id, undefined])));
          const chunkSize = 15; // maximum possible without maxNodeCount error
          const calls: Promise<ODataResult<any>>[] = [];
          for (let i = 0; i < missingIds.length; i += chunkSize) {
            const callIds = missingIds.slice(i, i + chunkSize);
            calls.push(this.employmentsApi.getOData({ $filter: callIds.map((id) => `id eq '${id}'`).join(" or ") }));
          }
          Promise.all(calls).then((results) => {
            results.forEach((result) => {
              result.items.forEach((item) => {
                this._employments[item.id] = item;
              });
            });
            this.$timeout();
          });
        }
      }
    }

    return this._employments;
  }
}